import { useState } from 'react';
import { motion } from 'framer-motion';
import { Quote, Star } from 'lucide-react';

const testimonials = [
  {
    initials: 'SP',
    photo: '/media/stacey-256.webp',
    name: 'Stacey Price',
    biz: 'PriceLock Realty',
    quote: (
      <>
        Noah was awesome to work with setting up our AI caller,{' '}
        <span className="font-semibold text-[#d4a10d]">Sarah</span>.
        He was{' '}
        <span className="font-semibold">right on point with timing and service</span>,
        stayed within the{' '}
        <span className="font-semibold">exact timeline he promised</span>, and his
        follow-up has been excellent. Even after setup, his continuing service has been
        great,{' '}
        <span className="font-semibold">super reliable and easy to work with</span>{' '}
        from start to finish.
      </>
    ),
  },
  {
    initials: 'CP',
    photo: '/media/curtis-256.webp',
    name: 'Curtis Prokopchuk',
    biz: 'REALTOR® | eXp Realty',
    quote: (
      <>
        He wasn't trying to force me into some{' '}
        <span className="font-semibold">cookie cutter software</span>. He built a
        solution around how I actually run my business.{' '}
        <span className="font-semibold">
          My phone never goes unanswered anymore
        </span>{' '}
         inbound calls are filtered through the AI assistant so I can focus on the
        highest value parts of my business instead of constantly getting interrupted.
        The outbound calling has also been powerful,{' '}
        <span className="font-semibold">
          instantly following up with new leads without everything relying on me personally.
        </span>
      </>
    ),
  },
];

function Avatar({ photo, initials, name }: { photo: string; initials: string; name: string }) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#d4a10d] to-[#b8900b] flex items-center justify-center text-white font-bold text-lg shadow-md flex-shrink-0">
        {initials}
      </div>
    );
  }

  return (
    <img
      src={photo}
      srcSet={`${photo.replace('-256.webp', '-128.webp')} 128w, ${photo} 256w`}
      sizes="48px"
      decoding="async"
      loading="lazy"
      width={48}
      height={48}
      alt={name}
      onError={() => setFailed(true)}
      className="w-12 h-12 rounded-full object-cover shadow-md flex-shrink-0 ring-2 ring-[#d4a10d]/30"
    />
  );
}

export default function SingleTestimonialCard() {
  return (
    <section className="py-8 sm:py-16 lg:py-24 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-5 sm:mb-12"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            What agents are saying
          </h2>
          <p className="text-lg text-gray-600">
            Real feedback from real estate professionals
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {testimonials.map(({ initials, photo, name, biz, quote }, idx) => (
            <motion.div
              key={name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: idx * 0.15 }}
              viewport={{ once: true }}
              className="relative group"
            >
              <div className="absolute -inset-1 bg-gradient-to-r from-[#d4a10d]/20 via-[#FFD700]/10 to-[#d4a10d]/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              <div className="relative bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-5 sm:p-8 md:p-10 hover:shadow-xl transition-all duration-500 group-hover:border-[#d4a10d]/30 hover:-translate-y-1 h-full flex flex-col">
                <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-[#d4a10d] to-[#b8900b] rounded-l-2xl group-hover:w-2.5 transition-all duration-300" />

                <div className="absolute -top-4 -left-2 text-[#d4a10d] opacity-10 pointer-events-none">
                  <Quote size={80} strokeWidth={1.5} />
                </div>

                <div className="hidden sm:flex gap-1 mb-5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-[#d4a10d] text-[#d4a10d]" />
                  ))}
                </div>

                <blockquote className="relative z-10 flex-1 flex flex-col">
                  <p className="hidden sm:block text-lg text-gray-800 leading-relaxed mb-7 flex-1">
                    {quote}
                  </p>

                  <details className="sm:hidden mb-3"><summary className="text-sm font-semibold cursor-pointer text-[#806008]">Read client review</summary><div className="text-base text-gray-700 leading-relaxed py-3">{quote}</div></details>
                  <footer className="flex items-center gap-4">
                    <Avatar photo={photo} initials={initials} name={name} />
                    <div>
                      <div className="font-bold text-gray-900">{name}</div>
                      <div className="text-gray-500 text-sm">{biz}</div>
                    </div>
                  </footer>
                </blockquote>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
